import { motion } from 'framer-motion';
import { useState, useEffect, useCallback } from 'react';
import { 
  Smartphone, 
  QrCode,
  Apple,
  PlayCircle,
  ArrowRight,
  CheckCircle,
  Monitor,
  Tablet,
  Star,
  Clock,
  ShoppingBag,
  Zap,
  Users
} from 'lucide-react';

const HomepageDownload = () => {
  const [userOS, setUserOS] = useState('');
  const [deviceType, setDeviceType] = useState('');

  // Enhanced device detection with memoization
  const detectDevice = useCallback(() => {
    const userAgent = window.navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
    const isTablet = /iPad|Android.*Tablet|Windows.*Touch/i.test(userAgent) && !isMobile;
    
    // Set device type
    if (isMobile) {
      setDeviceType('mobile');
    } else if (isTablet) {
      setDeviceType('tablet');
    } else {
      setDeviceType('desktop');
    }

    // Set OS
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      setUserOS('iOS');
    } else if (/Android/.test(userAgent)) {
      setUserOS('Android');
    } else if (/Windows/.test(userAgent)) {
      setUserOS('Windows');
    } else if (/Mac/.test(userAgent)) {
      setUserOS('macOS');
    } else {
      setUserOS('Unknown');
    }
  }, []);

  useEffect(() => {
    detectDevice();
  }, [detectDevice]);

  // App store URLs with better tracking
  const getAppStoreURL = (os) => {
    const baseUrls = {
      'iOS': 'https://apps.apple.com/app/dailzy-quick-commerce',
      'Android': 'https://play.google.com/store/apps/details?id=com.dailzy.app',
      'default': 'https://dailzy.com/download'
    };
    return baseUrls[os] || baseUrls.default;
  };

  // Smart QR code URL with better tracking
  const getSmartQRCodeURL = () => {
    return 'https://dailzy.app/download?source=qr&device=' + deviceType;
  };

  const handleQRClick = () => {
    const appStoreURL = getAppStoreURL(userOS);
    window.open(appStoreURL, '_blank');
  };

  const getOSIcon = () => {
    switch (userOS) {
      case 'iOS':
        return <Apple className="w-5 h-5" />;
      case 'Android':
        return <PlayCircle className="w-5 h-5" />;
      default:
        return <Smartphone className="w-5 h-5" />;
    }
  };

  const getDeviceIcon = () => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="w-5 h-5" />;
      case 'tablet':
        return <Tablet className="w-5 h-5" />;
      default:
        return <Monitor className="w-5 h-5" />;
    }
  };

  const getActionText = () => {
    switch (userOS) {
      case 'iOS':
        return 'Open App Store';
      case 'Android':
        return 'Open Google Play';
      default:
        return 'Get App Now';
    }
  };

  const getActionSubtext = () => {
    return `Direct link to ${userOS === 'iOS' ? 'App Store' : 'Google Play'}`;
  };

  const features = [
    { icon: Clock, text: "10-minute delivery guarantee" },
    { icon: ShoppingBag, text: "50,000+ products available" },
    { icon: Zap, text: "Real-time order tracking" },
    { icon: Star, text: "4.9★ rated by 2M+ users" },
    { icon: Users, text: "Supporting 10K+ local vendors" },
    { icon: CheckCircle, text: "Safe and secure payments" }
  ];

  return (
    <section className="min-h-screen bg-gray-50 relative overflow-hidden flex items-center">
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center max-w-7xl mx-auto h-full">
          
          {/* Left Side - Phone Mockups */}
          <motion.div 
            className="relative flex justify-center lg:justify-start"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="relative">
                             {/* Back Phone */}
               <motion.div 
                 className="absolute top-4 left-4 z-10"
                 initial={{ opacity: 0, y: 20 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.8, delay: 0.3 }}
                 viewport={{ once: true }}
               >
                 <div className="w-56 h-[420px] bg-gray-900 rounded-[2rem] p-2 shadow-2xl transform rotate-6">
                  <div className="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 rounded-[2rem] overflow-hidden relative">
                                         {/* Status bar */}
                     <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex justify-between items-center text-white">
                       <span className="text-xs font-semibold">9:41</span>
                       <div className="flex items-center space-x-1">
                         <div className="w-3 h-1 bg-white rounded-sm opacity-80"></div>
                         <div className="w-4 h-2 border border-white rounded-sm opacity-80">
                           <div className="w-full h-full bg-green-400 rounded-sm"></div>
                         </div>
                       </div>
                     </div>
                     
                     {/* App content - Order Tracking */}
                     <div className="px-3 py-4 h-full">
                       <div className="text-center mb-4">
                         <h3 className="text-white font-bold text-sm mb-2">Order Tracking</h3>
                         <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                           <div className="text-white">
                             <div className="text-xl font-bold mb-1">8 min</div>
                             <div className="text-xs opacity-90">Arriving soon</div>
                           </div>
                         </div>
                       </div>
                       
                       <div className="space-y-2">
                         <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg p-2">
                           <CheckCircle className="w-4 h-4 text-green-300" />
                           <div className="text-white text-xs">Order confirmed</div>
                         </div>
                         <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-lg p-2">
                           <CheckCircle className="w-4 h-4 text-green-300" />
                           <div className="text-white text-xs">Being prepared</div>
                         </div>
                         <div className="flex items-center gap-2 bg-white/30 backdrop-blur-sm rounded-lg p-2">
                           <div className="w-4 h-4 border-2 border-white rounded-full animate-pulse"></div>
                           <div className="text-white text-xs">Out for delivery</div>
                         </div>
                       </div>
                     </div>
                  </div>
                </div>
              </motion.div>
              
                             {/* Front Phone */}
               <motion.div 
                 className="relative z-20"
                 initial={{ opacity: 0, y: 30 }}
                 whileInView={{ opacity: 1, y: 0 }}
                 transition={{ duration: 0.8, delay: 0.1 }}
                 viewport={{ once: true }}
               >
                 <div className="w-64 h-[480px] bg-gray-900 rounded-[2.5rem] p-3 shadow-2xl">
                  <div className="w-full h-full bg-gradient-to-br from-red-500 to-orange-500 rounded-[2.5rem] overflow-hidden relative">
                                         {/* Status bar */}
                     <div className="bg-black/20 backdrop-blur-sm px-4 py-3 flex justify-between items-center text-white">
                       <span className="text-sm font-semibold">9:41</span>
                       <div className="flex items-center space-x-1">
                         <div className="w-4 h-2 bg-white rounded-sm opacity-80"></div>
                         <div className="w-5 h-2 border border-white rounded-sm opacity-80">
                           <div className="w-full h-full bg-green-400 rounded-sm"></div>
                         </div>
                       </div>
                     </div>
                     
                     {/* App content - Main Dashboard */}
                     <div className="px-4 py-5 h-full flex flex-col">
                       <div className="text-center mb-5">
                         <motion.div 
                           className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-lg"
                           animate={{ rotate: [0, 5, -5, 0] }}
                           transition={{ duration: 4, repeat: Infinity }}
                         >
                           <span className="text-2xl font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">D</span>
                         </motion.div>
                         <h3 className="text-white font-bold text-xl mb-1">Dailzy</h3>
                         <p className="text-white/80 text-xs">Quick Commerce</p>
                       </div>
                       
                       <motion.div 
                         className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 mb-4 text-center border border-white/20"
                         animate={{ scale: [1, 1.05, 1] }}
                         transition={{ duration: 3, repeat: Infinity }}
                       >
                         <div className="text-white">
                           <div className="text-3xl font-bold mb-1">10 min</div>
                           <div className="text-sm opacity-90">Delivery Promise</div>
                           <div className="text-xs opacity-70 mt-1">or it's free!</div>
                         </div>
                       </motion.div>
                       
                       <div className="grid grid-cols-2 gap-3 flex-1">
                         {['Groceries', 'Medicine', 'Electronics', 'More'].map((category, index) => (
                           <motion.div 
                             key={category}
                             className="bg-white/20 backdrop-blur-sm rounded-xl p-3 text-center border border-white/20 flex flex-col justify-center"
                             initial={{ opacity: 0, scale: 0.8 }}
                             animate={{ opacity: 1, scale: 1 }}
                             transition={{ delay: 1 + index * 0.2 }}
                             whileHover={{ scale: 1.05 }}
                           >
                             <div className="text-white font-medium text-xs">{category}</div>
                             {index === 0 && <div className="text-xs text-white/70 mt-1">50K+ items</div>}
                             {index === 1 && <div className="text-xs text-white/70 mt-1">24/7 available</div>}
                             {index === 2 && <div className="text-xs text-white/70 mt-1">Latest gadgets</div>}
                             {index === 3 && <div className="text-xs text-white/70 mt-1">& much more</div>}
                           </motion.div>
                         ))}
                       </div>
                     </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Content */}
          <motion.div 
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Header */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
                Download
                <span className="block text-transparent bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text">
                  Dailzy App
                </span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Get everything delivered in 15 minutes
              </p>
            </div>

            {/* Features List */}
            <motion.div 
              className="grid grid-cols-1 gap-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
            >
              {features.slice(0, 4).map((feature, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm">{feature.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Download Section */}
            <motion.div 
              className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="grid md:grid-cols-2 gap-6 items-center">
                
                {/* QR Code */}
                <div className="text-center">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="w-5 h-5 text-gray-700" />
                    <span className="text-sm font-semibold text-gray-900">Scan QR to download</span>
                  </div>
                  
                  <motion.div 
                    className="w-32 h-32 bg-white border-2 border-gray-200 rounded-xl flex items-center justify-center mx-auto shadow-sm relative overflow-hidden"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img 
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(getSmartQRCodeURL())}&color=000000&bgcolor=ffffff`}
                      alt="QR Code for Dailzy App" 
                      className="w-28 h-28 rounded-lg"
                    />
                    
                    {/* QR Code overlay with logo */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-5 h-5 bg-white rounded-md shadow-md flex items-center justify-center">
                        <span className="text-red-500 font-bold text-xs">D</span>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-3">
                  <motion.a
                    href={getAppStoreURL('iOS')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 p-3 bg-black hover:bg-gray-800 rounded-xl transition-all duration-300 group text-white"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Apple className="w-6 h-6 text-white" />
                    <div className="text-left">
                      <div className="text-xs text-gray-300">Download on the</div>
                      <div className="text-sm font-semibold">App Store</div>
                    </div>
                  </motion.a>
                  
                  <motion.a
                    href={getAppStoreURL('Android')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 p-3 bg-black hover:bg-gray-800 rounded-xl transition-all duration-300 group text-white"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <PlayCircle className="w-6 h-6 text-white" />
                    <div className="text-left">
                      <div className="text-xs text-gray-300">Get it on</div>
                      <div className="text-sm font-semibold">Google Play</div>
                    </div>
                  </motion.a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomepageDownload; 